import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Rocket, Zap, Building2, Wallet } from "lucide-react";

const ICONS = { free: Sparkles, starter: Zap, pro: Rocket, premium: Crown, enterprise: Building2 };

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 0 }).format(n || 0);
}

export default function SubscriptionsTab() {
    const [plans, setPlans] = useState([]);
    const [mine, setMine] = useState(null);
    const [loading, setLoading] = useState(null);
    const [activating, setActivating] = useState(false);

    const load = async () => {
        const [p, m] = await Promise.all([
            api.get("/subscriptions/plans"),
            api.get("/subscriptions/me"),
        ]);
        setPlans(p.data);
        setMine(m.data);
    };

    useEffect(() => { load(); }, []);

    const subscribe = async (code) => {
        setLoading(code);
        try {
            const { data } = await api.post("/subscriptions/subscribe", { code });
            toast.success(`Subscribed to ${data.plan.name}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(null);
        }
    };

    const activateFree = async () => {
        setActivating(true);
        try {
            await api.post("/wallet/topup", { amount: 100, method: "mpesa" });
            toast.success("Free plan activated. Wallet credited with KES 100.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setActivating(false);
        }
    };

    const currentCode = mine?.plan?.code;

    return (
        <div className="space-y-6" data-testid="subscriptions-page">
            <div>
                <h2 className="font-display text-3xl font-extrabold text-onego-navy">Plans &amp; subscriptions</h2>
                <p className="text-slate-600">Pick the plan that fits your stage. All plans are paid from your wallet balance.</p>
            </div>

            {/* Free activation banner */}
            {mine && currentCode === "free" && !mine.free_activated && (
                <div className="rounded-3xl p-1 og-gradient" data-testid="free-activation-banner">
                    <div className="rounded-[1.4rem] bg-white p-6 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 rounded-2xl og-gradient flex items-center justify-center text-white">
                                <Wallet />
                            </div>
                            <div>
                                <p className="font-display text-xl font-bold text-onego-navy">Activate your free plan</p>
                                <p className="text-sm text-slate-600">Top up your wallet with KES 100 to unlock the Free tier and start selling.</p>
                            </div>
                        </div>
                        <button onClick={activateFree} disabled={activating} className="og-btn-gradient disabled:opacity-60" data-testid="activate-free-btn">
                            {activating ? "Activating\u2026" : "Activate with KES 100"}
                        </button>
                    </div>
                </div>
            )}

            {/* Current plan */}
            {mine && (
                <div className="og-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="current-plan">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Your current plan</p>
                        <p className="font-display text-2xl font-extrabold text-onego-navy">{mine.plan.name}</p>
                        <p className="text-sm text-slate-500 mt-1">Wallet balance: KES {fmt(mine.wallet_balance)}</p>
                    </div>
                    <span className={`og-chip ${mine.free_activated ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                        {mine.free_activated ? "Active" : "Needs activation"}
                    </span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                {plans.map((p, i) => {
                    const Icon = ICONS[p.code] || Sparkles;
                    const isCurrent = currentCode === p.code;
                    const isHighlight = p.code === "pro";
                    return (
                        <div
                            key={p.code}
                            className={`relative og-card p-6 flex flex-col ${isHighlight ? "ring-2 ring-pink-500/40" : ""} ${isCurrent ? "ring-2 ring-onego-navy" : ""}`}
                            data-testid={`plan-card-${p.code}`}
                        >
                            {isHighlight && (
                                <span className="absolute -top-3 left-6 og-chip og-gradient text-white">Most popular</span>
                            )}
                            <div className="flex items-center justify-between">
                                <div className={`h-10 w-10 rounded-2xl flex items-center justify-center ${isHighlight ? "og-gradient text-white" : "bg-slate-100 text-onego-navy"}`}>
                                    <Icon size={18} />
                                </div>
                                {isCurrent && <span className="og-chip bg-onego-navy text-white">Current</span>}
                            </div>
                            <p className="mt-3 text-xs uppercase tracking-widest font-semibold text-slate-500">{p.tagline}</p>
                            <h3 className="font-display text-2xl font-extrabold text-onego-navy">{p.name}</h3>
                            <p className="mt-2 font-display text-3xl font-black text-onego-navy">
                                KES {fmt(p.price)} <span className="text-sm font-medium text-slate-500">/month</span>
                            </p>
                            <ul className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                                {p.features.map((f, j) => (
                                    <li key={j} className="flex items-start gap-2">
                                        <Check size={14} className="text-emerald-500 shrink-0 mt-0.5" /> {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                disabled={loading === p.code || isCurrent}
                                onClick={() => subscribe(p.code)}
                                className={`mt-5 w-full ${isHighlight ? "og-btn-gradient" : "og-btn-primary"} disabled:opacity-50`}
                                data-testid={`plan-subscribe-${p.code}`}
                            >
                                {isCurrent ? "Current plan" : loading === p.code ? "Subscribing\u2026" : p.price === 0 ? "Downgrade to Free" : `Subscribe • KES ${fmt(p.price)}`}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
